import type { CSSProperties } from "react";
import type { BlockBackground } from "@/types";

/** Allow-list of background colour tokens offered in the editor swatch picker
 *  (§5A). `dark` drives the auto-contrast: dark swatches flip text to on-dark. */
export const BACKGROUND_COLOR_TOKENS: { name: string; label: string; dark: boolean }[] = [
  { name: "slate", label: "Slate", dark: true },
  { name: "slate-800", label: "Slate 800", dark: true },
  { name: "slate-900", label: "Slate 900", dark: true },
  { name: "slate-600", label: "Slate 600", dark: true },
  // stone-blue is a mid-tone: the CD design sets dark (slate-900) text on it,
  // so it (and the lighter stone tints) are treated as LIGHT for auto-contrast.
  { name: "stone-blue", label: "Stone blue", dark: false },
  { name: "stone-200", label: "Stone 200", dark: false },
  { name: "stone-100", label: "Stone 100", dark: false },
  { name: "stone-600", label: "Stone 600", dark: true },
  { name: "paper-100", label: "Paper 100", dark: false },
  { name: "paper-50", label: "Paper 50", dark: false },
];

const DARK_TOKENS = new Set(BACKGROUND_COLOR_TOKENS.filter((t) => t.dark).map((t) => t.name));

/** A token name → CSS value (design token var); a hex passes through. */
export function resolveColor(color: string): string {
  return color.startsWith("#") ? color : `var(--${color})`;
}

/** Rough luminance for an arbitrary hex, so a custom colour still picks a side. */
function hexIsDark(hex: string): boolean {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (full.length < 6) return true;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  // perceived luminance (0–255)
  return 0.299 * r + 0.587 * g + 0.114 * b < 140;
}

/** Auto-contrast: does this background warrant on-dark (light) text? (§5A) */
export function isDarkBackground(bg?: BlockBackground): boolean {
  if (!bg) return false;
  if (bg.kind === "image") return true; // scrim makes it dark
  if (bg.kind === "color" && bg.color) {
    return bg.color.startsWith("#") ? hexIsDark(bg.color) : DARK_TOKENS.has(bg.color);
  }
  return false;
}

export const DEFAULT_OVERLAY = 70;

/** Inline style for the wrapper section given a background modifier. The image
 *  scrim is layered via a linear-gradient over the cover image.
 *
 *  Also publishes the band's own colour as `--band-bg`, so a chip that floats
 *  over band content (the hero's sticky scroll cue) can paint itself opaque
 *  against whatever the band is — no guessing, nothing showing through. */
export function backgroundStyle(bg?: BlockBackground): CSSProperties | undefined {
  if (!bg) return undefined;
  if (bg.kind === "color" && bg.color) {
    const color = resolveColor(bg.color);
    return { backgroundColor: color, "--band-bg": color } as CSSProperties;
  }
  if (bg.kind === "image" && bg.asset_url) {
    const overlay = clampOverlay(bg.overlay) / 100;
    const scrim = `rgba(19, 28, 32, ${overlay})`; // --slate-900 tint
    return {
      backgroundImage: `linear-gradient(${scrim}, ${scrim}), url("${cssUrl(bg.asset_url)}")`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      // No flat colour to borrow over a photo — the scrim's own tint, opaque.
      "--band-bg": "rgba(19, 28, 32, 0.92)",
    } as CSSProperties;
  }
  return undefined;
}

/** A background that actually paints something (has a colour or an image). */
export function hasVisibleBackground(bg?: BlockBackground): boolean {
  if (!bg) return false;
  if (bg.kind === "color") return !!bg.color;
  if (bg.kind === "image") return !!bg.asset_url;
  return false;
}

export function clampOverlay(value: number | undefined): number {
  if (typeof value !== "number" || Number.isNaN(value)) return DEFAULT_OVERLAY;
  return Math.min(100, Math.max(0, value));
}

/** Neutralise quotes/parentheses so the url() can't be broken out of. */
function cssUrl(url: string): string {
  return url.replace(/["()\\]/g, "");
}
